import { useState } from "react"
import { Link } from "react-router-dom"
import { Cookie, Settings } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog"
import { Switch } from "@/components/ui/switch"
import { useCookieConsent, COOKIE_POLICY_VERSION } from "@/hooks/useCookieConsent"
import { supabase } from "@/integrations/supabase/client"

export const CookieConsentBanner = () => {
  const { loaded, hasDecided, save, acceptAll, rejectAll } = useCookieConsent()
  const [open, setOpen] = useState(false)
  const [prefs, setPrefs] = useState({ functional: false, analytics: false, marketing: false })

  if (!loaded || hasDecided) return null

  const logToServer = async (cats: { functional: boolean; analytics: boolean; marketing: boolean }) => {
    try {
      await supabase.from("cookie_consent_log").insert({
        necessary: true,
        functional: cats.functional,
        analytics: cats.analytics,
        marketing: cats.marketing,
        policy_version: COOKIE_POLICY_VERSION,
        user_agent: navigator.userAgent.slice(0, 500),
      } as any)
    } catch {}
  }

  const handleAcceptAll = () => {
    acceptAll()
    logToServer({ functional: true, analytics: true, marketing: true })
  }
  const handleRejectAll = () => {
    rejectAll()
    logToServer({ functional: false, analytics: false, marketing: false })
  }
  const handleSaveCustom = () => {
    save({ necessary: true, ...prefs })
    logToServer(prefs)
    setOpen(false)
  }

  return (
    <>
      <div className="fixed bottom-4 left-4 right-4 sm:right-auto z-[100] w-auto sm:w-[380px] rounded-xl border bg-background shadow-lg p-4">
        <div className="flex items-start gap-2 mb-2">
          <Cookie className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <p className="text-sm font-semibold text-foreground">Usamos cookies</p>
        </div>
        <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
          Utilizamos cookies para melhorar sua experiência. Veja nossa{" "}
          <Link to="/politica-privacidade" className="text-primary underline underline-offset-2">Política de Privacidade</Link>.
        </p>
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => setOpen(true)} className="h-8 px-2 text-xs">
            Personalizar
          </Button>
          <Button variant="outline" size="sm" onClick={handleRejectAll} className="h-8 px-3 text-xs">
            Rejeitar
          </Button>
          <Button size="sm" onClick={handleAcceptAll} className="h-8 px-3 text-xs">
            Aceitar
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Preferências de cookies</DialogTitle>
            <DialogDescription>
              Conforme a LGPD (Lei 13.709/2018), você pode escolher quais cookies aceitar.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-start justify-between gap-4 p-3 rounded-lg bg-slate-50 dark:bg-slate-800">
              <div className="min-w-0">
                <p className="font-medium text-sm">Necessários</p>
                <p className="text-xs text-muted-foreground">Essenciais para login, segurança e funcionamento. Não podem ser desativados.</p>
              </div>
              <Switch checked disabled />
            </div>
            <div className="flex items-start justify-between gap-4 p-3 rounded-lg border">
              <div className="min-w-0">
                <p className="font-medium text-sm">Funcionais</p>
                <p className="text-xs text-muted-foreground">Lembrar preferências (tema, idioma, layout).</p>
              </div>
              <Switch checked={prefs.functional} onCheckedChange={(v) => setPrefs(p => ({ ...p, functional: v }))} />
            </div>
            <div className="flex items-start justify-between gap-4 p-3 rounded-lg border">
              <div className="min-w-0">
                <p className="font-medium text-sm">Analíticos</p>
                <p className="text-xs text-muted-foreground">Estatísticas anônimas de uso para melhorar o produto.</p>
              </div>
              <Switch checked={prefs.analytics} onCheckedChange={(v) => setPrefs(p => ({ ...p, analytics: v }))} />
            </div>
            <div className="flex items-start justify-between gap-4 p-3 rounded-lg border">
              <div className="min-w-0">
                <p className="font-medium text-sm">Marketing</p>
                <p className="text-xs text-muted-foreground">Mensurar campanhas e comunicações comerciais.</p>
              </div>
              <Switch checked={prefs.marketing} onCheckedChange={(v) => setPrefs(p => ({ ...p, marketing: v }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleRejectAll}>Rejeitar opcionais</Button>
            <Button onClick={handleSaveCustom}>Salvar preferências</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default CookieConsentBanner
